import { createAsyncThunk } from '@reduxjs/toolkit';
import { usersApi } from '../../api/usersApi/usersApi';
import { coursesApi } from '../../api/coursesApi/coursesApi';
import { eventsApi } from '../../api/events/eventsApi';

/**
 * Fetch dashboard statistics
 */
export const fetchDashboardStatsAsyncThunk = createAsyncThunk(
  'dashboard/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      // Fetch all data in parallel
      const [usersResponse, coursesResponse, eventsResponse] = await Promise.all([
        usersApi.getUsersbyFilters({ page: 1, limit: 1 }), // Just to get total count
        coursesApi.getCourses({ page: 1, limit: 1 }), // Just to get total count
        eventsApi.getEvents({ page: 1, limit: 100 }), // Get all events to filter
      ]);

      // Calculate current month start
      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      
      // Get total users count from pagination
      const totalUsers = usersResponse.pagination?.total || 0;
      
      // Fetch users to calculate monthly stats and active subscribers
      // We'll fetch multiple pages if needed, but start with first page
      const allUsersResponse = await usersApi.getUsersbyFilters({ 
        page: 1, 
        limit: 100, // Get first 100 to calculate stats (can be expanded if needed)
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      const allUsers = allUsersResponse.data || [];
      
      // If we have more users, we might need to fetch more, but for now use what we have
      // For accurate monthly count, we'd need to fetch all or use a date filter API
      
      // New users this month
      const newUsersThisMonth = allUsers.filter(user => {
        const userCreatedAt = new Date(user.createdAt);
        return userCreatedAt >= currentMonthStart;
      }).length;

      // Active subscribers (users with active subscriptions)
      const activeSubscribers = allUsers.filter(user => {
        return user.userSubscriptions && 
               user.userSubscriptions.length > 0 && 
               user.userSubscriptions.some(sub => sub.isActive === true);
      }).length;

      // Total courses published
      const totalCourses = coursesResponse.pagination?.total || (coursesResponse.data?.length || 0);

      // Upcoming live events (startTime > now)
      const allEvents = eventsResponse.data || [];
      const nowISO = new Date().toISOString();
      const upcomingLiveEvents = allEvents
        .filter(event => {
          const startTime = new Date(event.startTime);
          return startTime > new Date(nowISO);
        })
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
        .slice(0, 5); // Get top 5 upcoming

      return {
        totalUsers,
        newUsersThisMonth,
        activeSubscribers,
        totalCourses,
        upcomingLiveEvents,
        allUsers: allUsers.slice(0, 100), // Keep some users for reference
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || 'Failed to fetch dashboard statistics'
      );
    }
  }
);

